import React, { useRef, useState } from "react";
import { Panel, Label, Tabset, Tab, Button, showSnackbar, DynamicPanel } from "lib/components";
import { DesignerContext } from "lib/components/Context";
import { getComponentName } from "lib/components/ComponentUtil";
import { getLogger } from "lib/util";
import { useForceUpdate, useMountEffect } from "lib/util/Hooks";
import { Tools } from "./Tools";
import { findDesignerComponent, findDesignerParentAndIndex } from "./FindDesignerComponent";
import { PropertiesTable } from "./PropertiesTable";
import { OpenFileDropDown } from "./OpenFileDropDown";
import { isContainer } from "lib/components/JSONToJSX";
import { fetch } from "lib/util/fetch";
import { buildAPIUrl } from "core/utils/urls";

const log = getLogger("designer/DesignerMain");

export function DesignerMain() {
  const [selectedTool, setSelectedTool] = useState("Pointer");
  const [tabDefs, _setTabDefs] = useState([]);
  const [tabIndex, _setTabIndex] = useState(0);
  const [selectedComponents, _setSelectedComponents] = useState([]);
  const [openDropDownVisible, setOpenDropDownVisible] = useState();
  const tabDefsRef = useRef(tabDefs);
  const tabIndexRef = useRef(tabIndex);
  const selectedComponentsRef = useRef(selectedComponents);
  const contentUpdated = useForceUpdate();
  const setTabDefs = value => {
    tabDefsRef.current = value;
    _setTabDefs(value);
  }
  const setTabIndex = value => {
    tabIndexRef.current = value;
    _setTabIndex(value);
  }
  const setSelectedComponents = value => {
    selectedComponentsRef.current = value;
    _setSelectedComponents(value);
  }
  useMountEffect(() => getLastOpenTabs(tabDefs, setTabDefs, tabChanged));
  useMountEffect(() => window.addEventListener("keyup", (event) => {
    if (event.target != null && event.target.tagName === "BODY") {
      let defs = tabDefsRef.current;
      let index = tabIndexRef.current;
      let sel = selectedComponentsRef.current;
      let content = defs[index].content;
      log.info("KeyUp %o", event);
      if (event.code === "Delete") {
        log.debug("Delete event %o  %d   %o   %o", event, index, defs, sel);
        deleteSelectedComponents(content, sel, setSelectedComponents, contentUpdated);
      }
      else if (event.code === "ArrowUp" || event.code === "ArrowLeft") {
        if (event.ctrlKey)
          moveSelectedComponents(content, sel, setSelectedComponents, contentUpdated, -1);
        else if (event.altKey)
          setAdjacentComponentRowBreak(content, sel, setSelectedComponents, contentUpdated, -1, false);
        else
          selectAdjacentComponent(content, sel, setSelectedComponents, -1);
      }
      else if (event.code === "ArrowDown" || event.code === "ArrowRight") {
        if (event.ctrlKey)
          moveSelectedComponents(content, sel, setSelectedComponents, contentUpdated, 1);
        else if (event.altKey)
          setAdjacentComponentRowBreak(content, sel, setSelectedComponents, contentUpdated, 0, true);
        else
          selectAdjacentComponent(content, sel, setSelectedComponents, 1);
      }
      else if (event.key === '-') {
        selectParentComponent(sel, setSelectedComponents);
        log.debug("Selected parent %o", sel);
      }
      else
        log.debug("Keyup %o", event);
    }

  }));
  // addKeyListener(tabDefs, tabIndex, selectedComponents, setSelectedComponents);
  const tabs = [];
  for (let i = 0; i < tabDefs.length; i++) {
    let tabCaption = tabDefs[i].fileName;
    if (tabCaption == null)
      tabCaption = "New layout";
    let slashPos = tabCaption.lastIndexOf("/");
    if (slashPos >= 0)
      tabCaption = tabCaption.substring(slashPos + 1);
    let closeImage;
    if (tabDefs[i].changed === true)
      closeImage = "circle-empty-blue";
    tabs.push(
      <Tab key={`tab-${i}`} name={`tab-${i}`} caption={tabCaption} allowClose closeImage={closeImage}>
        <DesignerContext.Provider
          value={{
            getProperties: (componentContext, componentName) => {
              return getComponentProperties(
                selectedComponents,
                setSelectedComponents,
                componentContext,
                componentName
              );
            }
          }}
        >
          <DynamicPanel content={tabDefs[i].content} name="dynamic" fillRow noRowBreak />
        </DesignerContext.Provider>
      </Tab>
    );
  }

  function tabChanged(value) {
    setSelectedComponents([]);
    setTabIndex(value);
  }

  const changed = tabIndex < tabDefs.length && tabDefs[tabIndex] != null ? tabDefs[tabIndex].changed === true : false;
  const panelTabsetActions = (
    <Panel>
      <Button caption="New" onClick={() => createNewTab(tabDefs, setTabDefs, tabChanged, setSelectedComponents)} noRowBreak />
      <Button caption="Open" noRowBreak dropDownAlign="right" dropDownVisible={openDropDownVisible}
        dropDown={
          <OpenFileDropDown height={300}
            fileSelected={(value) => {
              openFile(value, tabDefs, setTabDefs, tabChanged);
              setOpenDropDownVisible(false);
            }}
          />
        }
        onClick={() => {
          setOpenDropDownVisible(true);
        }}
      />
      <Button caption="Save" enabled={changed} color="primary" onClick={() => saveLayout(tabDefs[tabIndex], contentUpdated)} />
    </Panel>
  );

  const headingProps = {
    backgroundColor: "rgb(220, 220, 220)",
    fillRow: true,
    fontBold: true,
    align: "center"
  };

  let currentContent;
  if (tabDefs != null && tabDefs.length > tabIndex && tabDefs[tabIndex] != null)
    currentContent = tabDefs[tabIndex].content;
  let tabsetOrMessage;
  if (tabDefs.length > 0)
    tabsetOrMessage = (
      <Tabset
        name="tabsetDesignerContent"
        selectedIndex={tabIndex}
        onChange={tabChanged}
        extraComponent={panelTabsetActions}
        onTabClose={(index) => closeTab(index, tabDefs, setTabDefs, tabIndex, setTabIndex)}
        fillHeight
        fillRow
      >
        {tabs}
      </Tabset>
    );
  else
    tabsetOrMessage = (
      <Panel fillRow>
        <Panel fillRow noRowBreak />
        {panelTabsetActions}
        <Label caption="Use the 'Open' or 'New' button to begin editing." align="center" marginTop={16} color="rgb(180, 180, 180)" fillRow />
      </Panel>
    );

  return (
    <Panel name="designerMain" fillHeight fillRow style={{ height: "100%" }}>
      <Panel
        fillHeight
        width={140}
        noRowBreak
        padding={0}
        borderRightWidth={1}
        borderRightColor="rgb(200, 200, 200)"
      >
        <Label caption="Tools" {...headingProps} />
        <Tools
          selected={selectedTool}
          setSelected={setSelectedTool}
          padding={0}
          fillRow
          fillHeight
          addComponent={(tool) => {
            addComponent(tabDefs[tabIndex].content, selectedComponents, setSelectedComponents, tool);
            contentUpdated();
          }}
        />
      </Panel>
      <Panel name="panelDesignerContent" padding={0} fillHeight fillRow noRowBreak>
        {tabsetOrMessage}
      </Panel>
      <Panel width={300} padding={0} borderLeftWidth={1} fillHeight borderLeftColor="rgb(200, 200, 200)">
        <Label caption="Properties" {...headingProps} />
        <PropertiesTable
          selectedComponents={selectedComponents}
          propertiesChanged={() => { if (tabDefs[tabIndex] != null) tabDefs[tabIndex].changed = true; contentUpdated(); }}
          content={currentContent}
          fillHeight
          padding={0}
          fillRow
        />
      </Panel>
    </Panel>
  );
}

async function saveLayout(tabDef, contentUpdated) {
  await fetch({
    url: buildAPIUrl("layout/" + tabDef.fileName),
    method:"put",
    data: JSON.parse(JSON.stringify(tabDef.content))
  })
    .then((response) => {
      tabDef.changed = false;
      contentUpdated();
      showSnackbar("Layout saved.");
    })
    .catch((reason) => showSnackbar("Error saving layout - " + reason));
}

function deleteFromArray(array, index) {
  let result = [];
  for (let i = 0; i < array.length; i++)
    if (i !== index)
      result.push(array[i]);
  return result;
}

function moveSelectedComponents(content, selectedComponents, setSelectedComponents, contentUpdated, moveBy) {
  if (selectedComponents.length !== 1)
    showSnackbar("You can only select one component to move.");
  else {
    let parentAndIndex = findDesignerParentAndIndex(content, selectedComponents[0]);
    let otherCompIndex = parentAndIndex.index + moveBy;
    if (otherCompIndex < 0)
      showSnackbar("The selected component is already first in its container.");
    else if (otherCompIndex >= parentAndIndex.parent.children.length)
      showSnackbar("The selected component is already last in its container.");
    else {
      let sel = parentAndIndex.parent.children[parentAndIndex.index];
      parentAndIndex.parent.children[parentAndIndex.index] = parentAndIndex.parent.children[otherCompIndex];
      parentAndIndex.parent.children[otherCompIndex] = sel;
      contentUpdated();
      setSelectedComponents([]);
      setSelectedComponents(selectedComponents);
    }
  }
}

function selectParentComponent(sel, setSelectedComponents) {
  if (sel.length === 0)
    showSnackbar("You need to select a component before you can use the minus key to select its parent.");
  else
    setSelectedComponents([sel[0].substring(0, sel[0].lastIndexOf("."))]);
}

function selectAdjacentComponent(content, sel, setSelectedComponents, offset) {
  if (sel.length === 0)
    showSnackbar("You have to select a component before you can us the arrow keys to select the previous/next components.");
  else {
    let parentAndIndex = findDesignerParentAndIndex(content, sel[0]);
    if (parentAndIndex != null) {
      let newIndex = parentAndIndex.index + offset;
      let children = parentAndIndex.parent.children;
      if (newIndex < 0 || newIndex >= children.length)
        return;
      log.info("select relative content %o  sel %o   P&I %o   children %o index %d", content, sel, parentAndIndex, children, newIndex);
      let newSel = sel[0].substring(0, sel[0].lastIndexOf(".") + 1) + children[newIndex].name;
      setSelectedComponents([newSel]);
    }
  }
}

function setAdjacentComponentRowBreak(content, sel, setSelectedComponents, contentUpdated, offset, value) {
  if (sel.length === 0)
    showSnackbar("You have to select a component before you can us the arrow keys to change row break properties.");
  else {
    let parentAndIndex = findDesignerParentAndIndex(content, sel[0]);
    if (parentAndIndex != null) {
      let newIndex = parentAndIndex.index + offset;
      let children = parentAndIndex.parent.children;
      if (newIndex < 0 || newIndex >= children.length)
        return;
      children[newIndex].rowBreak = value;
      log.info("set component rowbreak %o  sel %o   P&I %o   children %o index %d", content, sel, parentAndIndex, children, newIndex);
      setSelectedComponents([]);
      setSelectedComponents(sel);
      contentUpdated();
    }
  }
}



function deleteSelectedComponents(content, selectedComponents, setSelectedComponents, contentUpdated) {
  for (let i = 0; i < selectedComponents.length; i++) {
    let parentAndIndex = findDesignerParentAndIndex(content, selectedComponents[i]);
    parentAndIndex.parent.children = deleteFromArray(parentAndIndex.parent.children, parentAndIndex.index);
    log.debug("After delete: %o   for  %o", parentAndIndex, selectedComponents[i]);
  }
  setSelectedComponents([]);
  contentUpdated();
}

function closeTab(index, tabDefs, setTabDefs, tabIndex, setTabIndex) {
  let newTabs = deleteFromArray(tabDefs, index);
  setTabDefs(newTabs);
  if (tabIndex >= newTabs.length)
    setTabIndex(newTabs.length - 1);
  saveOpenTabs(newTabs);
}

function getComponentProperties(selectedComponents, setSelectedComponents, componentContext, compName) {
  let fullName = getComponentName(componentContext, compName);
  log.debug("Full name for %s is %s.  Content is %o", compName, fullName, componentContext);
  const result = { style: {} };
  if (selectedComponents.includes(fullName))
    result.style.border = "1px dashed black";
  result.style.cursor = "pointer";
  result.onClick = (event) => {
    componentClicked(selectedComponents, setSelectedComponents, fullName, event.ctrlKey);
    event.stopPropagation();
  };
  return result;
}

function componentClicked(selectedComponents, setSelectedComponents, componentName, ctrlDown) {
  if (ctrlDown) {
    const index = selectedComponents.indexOf(componentName);
    if (index < 0)
      setSelectedComponents([...selectedComponents, componentName]);
    else {
      let newSel = deleteFromArray(selectedComponents, index);
      setSelectedComponents(newSel);
    }
  }
  else
    setSelectedComponents([componentName]);
  log.debug("Clicked %s while in designer", componentName);
}

function addComponent(content, selectedComponents, setSelectedComponents, tool) {
  if (selectedComponents == null || selectedComponents.length !== 1) {
    showSnackbar("You need to have a single container selected to add a component.");
    return;
  }
  let selName = selectedComponents[0];
  let lastName = selName.substring(selName.lastIndexOf(".") + 1);
  let parent = findDesignerComponent(content, selName);
  if (parent == null) {
    showSnackbar("Couldn't find parent container.  See console for more details.");
    log.error("Parent container not found for %o with content %o", selName, content);
    return;
  }
  while (parent != null && !isContainer(parent)) {
    let index = selName.lastIndexOf(".");
    if (index < 0)
      parent = null;
    else {
      selName = selName.substring(0, index);
      parent = findDesignerComponent(content, selName);
    }
  }
  if (parent == null) {
    showSnackbar("Could find a parent container in the heirarchy.");
    return;
  }
  if (parent.children == null)
    parent.children = [];
  const newComp = { name: allocateDefaultName(content, tool), type: tool };
  let insertIndex = 0;
  for (insertIndex = 0; insertIndex < parent.children.length; insertIndex++)
    if (parent.children[insertIndex].name === lastName)
      break;
  parent.children.splice(insertIndex + 1, 0, newComp);
  // parent.children.push(newComp);
  setSelectedComponents([selName + "." + newComp.name]);
}

function allocateDefaultName(content, prefix) {
  const compsByName = {};
  addComponentsByName(content, compsByName);
  log.debug("Alloc %s in %o   By name: %o", prefix, content, compsByName);
  for (let i = 1; i < 9999; i++)
    if (compsByName[prefix + i] === undefined)
      return prefix + i;
  return `Wow, you must have a lot of ${prefix} on this page.`;
}

function addComponentsByName(content, compsByName) {
  for (let i = 0; i < content.length; i++) {
    const name = content[i].name;
    if (name != null)
      compsByName[name] = true;
    if (content[i].children != null)
      addComponentsByName(content[i].children, compsByName);
  }
}

function blankTab() {
  return {
    fileName: null, changed: true, content: [
      { type: "panel", name: "panelRoot", fillRow: true }]
  };
}

function createNewTab(tabDefs, setTabDefs, setTabIndex, setSelectedComponents) {
  const newDefs = [...tabDefs, blankTab()];
  setTabDefs(newDefs);
  setTabIndex(newDefs.length - 1);
  setSelectedComponents(["designerMain.panelDesignerContent.tab-panel-" + (newDefs.length - 1) + ".undefined.panelRoot"]);
}

function openFile(file, tabDefs, setTabDefs, tabChanged) {
  for (let i = 0; i < tabDefs.length; i++) {
    if (tabDefs[i].fileName === file) {
      tabChanged(i);
      return;
    }
  }
  addTabForFile(file, tabDefs, setTabDefs, tabChanged, true);
}

async function addTabForFile(file, tabDefs, setTabDefs, tabChanged, remember) {
//could not fully test this after converting to axios
 await fetch({url: buildAPIUrl("layout/" + file)})
    .then((result) => {
      result.data.fileName = file;
      const newDefs = [...tabDefs, result.data];
      setTabDefs(newDefs);
      if (remember) {
        tabChanged(newDefs.length - 1);
        saveOpenTabs(newDefs);
      }
    })
    .catch((reason) => {
      showSnackbar(`Error opening layout - ${reason}`);
    });
}

function saveOpenTabs(tabDefs) {
  let openTabs = "";
  for (let i = 0; i < tabDefs.length; i++)
    if (tabDefs[i].fileName != null)
      openTabs += tabDefs[i].fileName + ",";
  if (openTabs.endsWith(","))
    openTabs = openTabs.substring(0, openTabs.length - 1);
  log.info("New open %s", openTabs);
  localStorage.setItem("designer.openTabs", openTabs);
}

function getLastOpenTabs(tabDefs, setTabDefs, tabChanged) {
  let openTabs = localStorage.getItem("designer.openTabs");
  log.info("Last open %s", openTabs);
  if (openTabs == null || openTabs.length === 0)
    setTabDefs([blankTab()]);
  else {
    let files = openTabs.split(",");
    for (let i = 0; i < files.length; i++)
      addTabForFile(files[i], tabDefs, setTabDefs, tabChanged, false);
  }
}
